"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  buildThesisFrames,
  canFrameThesis,
  NOTE_ROWS,
  notesText,
  noteText,
  parseTones,
  SOAPSTONE_THESIS_FRAMES,
  SOAPSTONE_TONE_WORDS,
  type SoapField,
  type SoapInput,
  TEXT_TYPES,
  type TextType,
  toggleTone,
} from "@/lib/tool-kit/essay/soapstone";

/** English strings. A translated content file overrides these through `ui`. */
export const SOAPSTONE_ANALYSIS_GENERATOR_STRINGS = {
  textTypeLabel: "Type of text",
  textTypes: {
    speech: "Speech",
    essay: "Essay or article",
    letter: "Letter",
    other: "Other text",
  },
  addressing: {
    speech: "speaking to",
    essay: "writing for",
    letter: "writing to",
    other: "addressing",
  },
  titleLabel: "Title of the text",
  titlePlaceholder: "e.g. the Gettysburg Address",
  fields: {
    speaker: "Speaker",
    speakerContext: "Who the speaker is (optional)",
    occasion: "Occasion",
    audience: "Audience",
    purpose: "Purpose",
    subject: "Subject",
    tone: "Tone",
    choices: "Rhetorical choices you will analyze (optional)",
  },
  placeholders: {
    speaker: "e.g. Abraham Lincoln",
    speakerContext:
      "e.g. the sixteenth president, leading the Union midway through the Civil War",
    occasion:
      "e.g. the dedication of a national cemetery at Gettysburg in November 1863",
    audience:
      "e.g. the assembled crowd and, through newspapers, the Union public",
    purpose:
      "e.g. to honor the Union dead and rally the nation to finish the war",
    subject: "e.g. the meaning of the soldiers' sacrifice",
    tone: "e.g. solemn, reverent, resolute",
    choices: "e.g. repetition, antithesis, and imagery of birth and death",
  },
  hints: {
    speaker: "The name as you will write it in your essay.",
    speakerContext:
      "Their position, background, or bias — whatever gives them authority here.",
    occasion: "The time, place, and event or debate that prompted the text.",
    audience: "Who it was written for, including any wider or later audience.",
    purpose:
      "What the speaker wants the audience to think, feel, or do. Start with “to”.",
    subject: "The topic — and the larger issue underneath it.",
    tone: "Two or three precise adjectives. Click words in the bank below to add or remove them.",
    choices:
      "The devices and moves your essay will analyze. The thesis frames use them.",
  },
  noteLabels: {
    speaker: "Speaker",
    occasion: "Occasion",
    audience: "Audience",
    purpose: "Purpose",
    subject: "Subject",
    tone: "Tone",
    choices: "Rhetorical choices",
  },
  letters: {
    speaker: "S",
    occasion: "O",
    audience: "A",
    purpose: "P",
    subject: "S",
    tone: "T",
    choices: "+",
  },
  toneBankTitle: "Tone word bank — click to add or remove",
  toneGroups: {
    approving: "Approving",
    critical: "Critical",
    ironic: "Humorous or ironic",
    somber: "Somber",
    objective: "Objective",
    forceful: "Urgent or forceful",
    uneasy: "Uneasy or doubtful",
  },
  toneWords: SOAPSTONE_TONE_WORDS,
  tryExample: "Try an example",
  clear: "Clear",
  notesHeading: "Your SOAPSTone notes",
  notesTitle: "SOAPSTone notes: {title}",
  notesTitleUntitled: "SOAPSTone notes",
  notesEmpty: "Not filled in yet",
  noteLine: "{label}: {text}",
  copyNotes: "Copy notes",
  pushFurtherLabel: "Push further:",
  pushFurther: {
    speaker:
      "What in their background or position gives them authority — or a bias — on this subject?",
    occasion:
      "What event, debate, or moment prompted the text, and what was at stake right then?",
    audience:
      "Who is the primary audience, and is there a wider one? What do they already believe?",
    purpose:
      "What should the audience think, feel, or do afterwards? Is there a second purpose?",
    subject:
      "Beyond the topic itself, what larger value or question is at issue?",
    tone: "Which specific words create this tone? Does it shift anywhere in the text?",
    choices:
      "For each choice, how does it serve the purpose with this audience?",
  },
  thesisHeading: "Thesis frames for a rhetorical analysis",
  thesisIntro:
    "Each frame combines your notes into an opening claim — honest templates, no AI. Bracketed words mark notes you have not filled in.",
  thesisLabels: [
    "Choices and purpose",
    "Audience, occasion, and tone",
    "Choices, subject, and tone",
    "Line of reasoning — fill in the blanks",
  ],
  needThesis: "Add the speaker and the purpose to see thesis frames.",
  missing: {
    title: "[title]",
    speaker: "[speaker]",
    occasion: "[occasion]",
    audience: "[audience]",
    purpose: "[purpose]",
    subject: "[subject]",
    tone: "[tone]",
    choices: "[rhetorical choices]",
  },
  and: "and",
  at: "at",
  copy: "Copy",
  copied: "Copied!",
  thesisFrames: SOAPSTONE_THESIS_FRAMES,
  example: {
    title: "the Gettysburg Address",
    speaker: "Abraham Lincoln",
    speakerContext:
      "the sixteenth president of the United States, leading the Union midway through the Civil War",
    occasion:
      "the dedication of the Soldiers' National Cemetery at Gettysburg in November 1863",
    audience:
      "the assembled crowd and, through newspaper reprints, the wider Union public",
    purpose:
      "to honor the Union dead and rally listeners to finish the war for a nation dedicated to equality",
    subject:
      "the meaning of the soldiers' sacrifice and the survival of democratic government",
    tone: "solemn, reverent, resolute",
    choices: "repetition, antithesis, and imagery of birth and death",
  },
} as const;

type Strings = typeof SOAPSTONE_ANALYSIS_GENERATOR_STRINGS;

type ToneGroup = keyof typeof SOAPSTONE_TONE_WORDS;

type AreaField = "occasion" | "audience" | "purpose" | "subject" | "choices";

const TONE_GROUPS = Object.keys(SOAPSTONE_TONE_WORDS) as ToneGroup[];

const EMPTY_INPUT: SoapInput = {
  title: "",
  speaker: "",
  speakerContext: "",
  occasion: "",
  audience: "",
  purpose: "",
  subject: "",
  tone: "",
  choices: "",
};

const CHIP =
  "rounded-full border border-border bg-card px-2.5 py-0.5 text-foreground text-xs transition-colors hover:bg-muted aria-pressed:border-primary aria-pressed:bg-primary/10 aria-pressed:text-primary";

const LETTER_BADGE =
  "inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-primary font-semibold text-primary-foreground text-xs";

function ToneBank({
  tone,
  onToggle,
  strings,
}: {
  tone: string;
  onToggle: (word: string) => void;
  strings: Strings;
}) {
  const chosen = new Set(parseTones(tone));
  return (
    <details className="rounded-lg border border-border bg-muted/30 p-3" open>
      <summary className={`${LABEL} cursor-pointer`}>
        {strings.toneBankTitle}
      </summary>
      <div className="mt-3 space-y-3">
        {TONE_GROUPS.map((group) => (
          <div className="space-y-1.5" key={group}>
            <p className="font-medium text-muted-foreground text-xs">
              {strings.toneGroups[group]}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {strings.toneWords[group].map((word) => (
                <button
                  aria-pressed={chosen.has(word)}
                  className={CHIP}
                  key={word}
                  onClick={() => onToggle(word)}
                  type="button"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

/*
 * A list, not a <dl>: the tool page's FAQ check reads every dt/dd pair on the page as
 * a question and answer, and these notes are neither.
 */
function Notes({ input, strings }: { input: SoapInput; strings: Strings }) {
  return (
    <ul className="divide-y divide-border rounded-lg border border-border bg-card">
      {NOTE_ROWS.map((row) => {
        const text = noteText(input, row);
        return (
          <li className="grid gap-2 p-4 sm:grid-cols-[11rem_1fr]" key={row}>
            <p className="flex items-start gap-2">
              <span aria-hidden="true" className={LETTER_BADGE}>
                {strings.letters[row]}
              </span>
              <span className="font-medium text-foreground text-sm">
                {strings.noteLabels[row]}
              </span>
            </p>
            <div className="space-y-1">
              {text === "" ? (
                <p className="text-muted-foreground text-sm italic">
                  {strings.notesEmpty}
                </p>
              ) : (
                <p className="text-foreground text-sm leading-relaxed">
                  {text}
                </p>
              )}
              <p className={HINT}>
                <span className="font-medium">{strings.pushFurtherLabel}</span>{" "}
                {strings.pushFurther[row]}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function SoapstoneAnalysisGeneratorWidget() {
  const strings = useToolUi(SOAPSTONE_ANALYSIS_GENERATOR_STRINGS);
  const [input, setInput] = useState<SoapInput>(EMPTY_INPUT);
  const [textType, setTextType] = useState<TextType>("speech");

  function update(field: SoapField, value: string) {
    setInput((previous) => ({ ...previous, [field]: value }));
  }

  const theses = canFrameThesis(input)
    ? buildThesisFrames(
        input,
        textType,
        {
          addressing: strings.addressing,
          missing: strings.missing,
          and: strings.and,
          at: strings.at,
        },
        strings.thesisFrames
      )
    : [];

  const typeOptions = TEXT_TYPES.map((value) => ({
    value,
    label: strings.textTypes[value],
  }));

  const area = (field: AreaField, rows = 2) => (
    <TextAreaField
      hint={strings.hints[field]}
      id={`sag-${field}`}
      label={strings.fields[field]}
      onChange={(value) => update(field, value)}
      placeholder={strings.placeholders[field]}
      prose
      rows={rows}
      value={input[field]}
    />
  );

  return (
    <>
      <div className={`${CARD} space-y-5`}>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="sag-type"
            label={strings.textTypeLabel}
            onChange={setTextType}
            options={typeOptions}
            value={textType}
          />
          <TextField
            id="sag-title"
            label={strings.titleLabel}
            onChange={(value) => update("title", value)}
            placeholder={strings.titlePlaceholder}
            value={input.title}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.hints.speaker}
            id="sag-speaker"
            label={strings.fields.speaker}
            onChange={(value) => update("speaker", value)}
            placeholder={strings.placeholders.speaker}
            value={input.speaker}
          />
          <TextField
            hint={strings.hints.speakerContext}
            id="sag-context"
            label={strings.fields.speakerContext}
            onChange={(value) => update("speakerContext", value)}
            placeholder={strings.placeholders.speakerContext}
            value={input.speakerContext}
          />
        </div>
        {area("occasion")}
        {area("audience")}
        {area("purpose")}
        {area("subject", 1)}
        <TextField
          hint={strings.hints.tone}
          id="sag-tone"
          label={strings.fields.tone}
          onChange={(value) => update("tone", value)}
          placeholder={strings.placeholders.tone}
          value={input.tone}
        />
        <ToneBank
          onToggle={(word) => update("tone", toggleTone(input.tone, word))}
          strings={strings}
          tone={input.tone}
        />
        {area("choices")}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput({ ...strings.example })}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput(EMPTY_INPUT)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.notesHeading}</h2>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyNotes}
            small
            text={notesText(input, strings)}
          />
        </div>
        <Notes input={input} strings={strings} />
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.thesisHeading}</h2>
        {theses.length === 0 ? (
          <Notice>{strings.needThesis}</Notice>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              {strings.thesisIntro}
            </p>
            {theses.map((thesis, index) => (
              <div
                className={`${RESULT_CARD} space-y-2`}
                key={strings.thesisLabels[index] ?? thesis}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className={WIDGET_H3}>{strings.thesisLabels[index]}</h3>
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copy}
                    small
                    text={thesis}
                  />
                </div>
                <p className="text-foreground text-sm leading-relaxed">
                  {thesis}
                </p>
              </div>
            ))}
          </>
        )}
      </section>
    </>
  );
}
